import { IconButton } from "$components/button/icon-button";
import { ConditionalDisplay } from "$components/conditional-display";
import { makeStyles } from "$components/mui-styles";
import { Box, Grid, Typography } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { FormattedMessage, useIntl } from "react-intl";
import { InvoiceStatus } from "../../InvoiceFilters/types";
import type { InvoiceCardSummaryProps } from "../types";
import { InvoiceStatusChip } from "./InvoiceStatusChip";

const useStyles = makeStyles({
  expandIcon: {
    transform: ({ expanded }: { expanded: boolean }) =>
      expanded ? "rotate(0deg)" : "rotate(180deg)",
    transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  },
  capitalize: {
    textTransform: "capitalize",
  },
});

export const InvoiceSummary = ({
  expanded,
  setExpanded,
  invoiceNumber,
  invoiceStatus,
  created,
  creator,
  patientType,
  balance,
  total,
  payers,
  facility,
  organisation,
}: InvoiceCardSummaryProps) => {
  const classes = useStyles({ expanded });
  const { formatDate, formatMessage } = useIntl();

  const expandButtonLabel = expanded
    ? formatMessage({
        id: "invoicing.invoice.show-summary-view",
        defaultMessage: "Hide Invoice Details",
      })
    : formatMessage({
        id: "invoicing.invoice.show-detail-view",
        defaultMessage: "Show Invoice Details",
      });

  const toggleExpanded = () => setExpanded(!expanded);

  const hasPatientPayer = !!payers.find(
    (p) => p.type === "PATIENT" && p.balance.paid > 0,
  );

  let displayStatus = invoiceStatus;

  // Only display an invoice as partially paid if a patient has paid something
  if (invoiceStatus === InvoiceStatus.PartiallyPaid && !hasPatientPayer) {
    displayStatus = InvoiceStatus.Unpaid;
  }

  return (
    <Grid
      container
      justifyContent="space-between"
      alignItems="center"
      onClick={toggleExpanded}
    >
      <Grid item xs={8}>
        <Typography variant="h6">
          <FormattedMessage
            id="invoicing.invoice.summary.invoiceNumber"
            defaultMessage="Invoice {invoiceNumber}"
            values={{ invoiceNumber }}
          />
        </Typography>
      </Grid>
      <Grid item xs>
        <Box textAlign="right" mr={1}>
          <InvoiceStatusChip status={displayStatus} />
        </Box>
      </Grid>
      <Grid item>
        <IconButton onClick={toggleExpanded} aria-label={expandButtonLabel}>
          <ExpandMoreIcon className={classes.expandIcon} />
        </IconButton>
      </Grid>
      <Grid item xs={8}>
        <Box display="inline-block" mb="8px">
          <Typography variant="body2">
            <FormattedMessage
              id="invoicing.invoice.summary.facilityName"
              defaultMessage="Facility: {facilityName}"
              values={{ facilityName: facility.name }}
            />
          </Typography>

          <Typography variant="body2">
            <FormattedMessage
              id="invoicing.invoice.summary.organisationName"
              defaultMessage="Organisation: {organisationName}"
              values={{ organisationName: organisation.name }}
            />
          </Typography>
        </Box>
      </Grid>
      <ConditionalDisplay condition={!expanded}>
        <Grid item xs={7}>
          <Typography variant="subtitle2">
            <b>{patientType}</b>
          </Typography>
          <Typography variant="subtitle2">
            <ConditionalDisplay condition={!!created}>
              <Box display="inline-block" mr={2}>
                <FormattedMessage
                  id="invoicing.invoice.summary.created"
                  defaultMessage="Invoiced on {created}"
                  values={{
                    created: formatDate(new Date(created), {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    }),
                  }}
                />
              </Box>
            </ConditionalDisplay>
            <span className={classes.capitalize}>
              <FormattedMessage
                id="invoicing.invoice.summary.creator"
                defaultMessage="By: {creator}"
                values={{ creator }}
              />
            </span>
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Box textAlign="right">
            <Typography variant="h6">
              <ConditionalDisplay condition={invoiceStatus === "DRAFT"}>
                <FormattedMessage
                  id="invoicing.invoice.summary.total"
                  defaultMessage="Total: {total}"
                  values={{ total }}
                />
              </ConditionalDisplay>
              <ConditionalDisplay condition={invoiceStatus !== "DRAFT"}>
                <FormattedMessage
                  id="invoicing.invoice.summary.balance"
                  defaultMessage="Balance: {balance}"
                  values={{ balance }}
                />
              </ConditionalDisplay>
            </Typography>
          </Box>
        </Grid>
      </ConditionalDisplay>
    </Grid>
  );
};
